import { create } from 'zustand'
import type { ModuleId, Taal, Video } from '../content/content'
import { STANDAARD_INVOER } from '../energie/rekenmotor'
import type { EnergieInvoer } from '../energie/rekenmotor'
import { eindeSessie, meet } from '../kiosk/meting'

export type Scherm = 'hoofdscherm' | ModuleId

interface KioskState {
  scherm: Scherm
  taal: Taal
  /**
   * De keuze binnen een onderdeel: in Automatisering de hotspot, in
   * Veiligheid de herkende uitdaging. Eén veld, want een bezoeker zit maar in
   * één onderdeel tegelijk en `ga()` wist hem bij elke wissel.
   */
  hotspot: string | null
  /** Blokkeert invoer zolang een cameravlucht loopt (§2.4). */
  vergrendeld: boolean
  /** Product-id waarvan de 3D-viewer openstaat, of null. */
  productViewer: string | null
  /**
   * De film die in het videovenster staat, of null. Hij gaat alleen open op
   * de filmknop: sinds 21 oktober start geen film meer vanzelf. Open is niet
   * hetzelfde als spelend: `videoSpeelt` zegt of de inactiviteitsteller moet
   * wachten (PLAN.md §3 — de reset gaat niet af terwijl er een video speelt).
   */
  video: Video | null
  videoSpeelt: boolean
  /** De instellingen van de energiecalculator (flowchart: vier instellingen). */
  energie: EnergieInvoer

  ga: (scherm: Scherm) => void
  kiesHotspot: (id: string | null) => void
  zetTaal: (taal: Taal) => void
  zetVergrendeld: (v: boolean) => void
  toonProduct: (id: string | null) => void
  startVideo: (video: Video) => void
  stopVideo: () => void
  zetVideoSpeelt: (v: boolean) => void
  zetEnergie: (veld: keyof EnergieInvoer, waarde: number) => void
  /** Harde sessie-reset: wist alles wat bezoekergebonden is (§3). */
  reset: () => void
}

const SCHOON = {
  scherm: 'hoofdscherm' as Scherm,
  taal: 'nl' as Taal,
  hotspot: null,
  vergrendeld: false,
  productViewer: null,
  video: null,
  videoSpeelt: false,
  energie: STANDAARD_INVOER,
}

/** `/video/k-matic.mp4` → `k-matic`. */
const filmNaam = (src: string) => src.split('/').pop()?.replace(/\.[a-z0-9]+$/, '') ?? 'film'

/**
 * Elke actie die een bezoeker met een tik start, telt ook voor de monitoring
 * (`kiosk/meting.ts`, docs/beheer.md). Wat de code zelf doet — de reset, het
 * vergrendelen tijdens een vlucht, een film die afloopt — telt niet.
 */
export const useKiosk = create<KioskState>((set, get) => ({
  ...SCHOON,

  ga: (scherm) => {
    meet(scherm === 'hoofdscherm' ? 'terug' : `tegel:${scherm}`)
    set({ scherm, hotspot: null, vergrendeld: true, productViewer: null, video: null, videoSpeelt: false })
  },
  kiesHotspot: (hotspot) => {
    if (hotspot) meet(`keuze:${get().scherm}:${hotspot}`)
    set({ hotspot, video: null, videoSpeelt: false })
  },
  zetTaal: (taal) => {
    meet(`taal:${taal}`)
    set({ taal })
  },
  zetVergrendeld: (vergrendeld) => set({ vergrendeld }),
  toonProduct: (productViewer) => {
    if (productViewer) meet(`3d:${productViewer}`)
    set({ productViewer })
  },
  startVideo: (video) => {
    meet(`film:${filmNaam(video.src)}`)
    set({ video })
  },
  stopVideo: () => set({ video: null, videoSpeelt: false }),
  zetVideoSpeelt: (videoSpeelt) => set({ videoSpeelt }),
  zetEnergie: (veld, waarde) => {
    meet(`energie:${veld}`)
    set((s) => ({ energie: { ...s.energie, [veld]: waarde } }))
  },
  reset: () => {
    eindeSessie()
    set({ ...SCHOON })
  },
}))
